import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { OperatorLogo } from '../../components/common/BrandLogo';
import {
  ClipboardList,
  RefreshCw,
  CheckCircle2,
  Copy,
  Check,
} from 'lucide-react';

export const AdminOrdersPage: React.FC = () => {
  const {
    orders,
    packages,
    loading,
    actionLoading,
    refresh,
    claimOrder,
    confirmDelivered,
  } = useAdmin();

  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'processing' | 'completed'>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Delivery confirmation dialog
  const [deliveringOrderId, setDeliveringOrderId] = useState<string | null>(null);
  const [deliveryRef, setDeliveryRef] = useState('');

  const handleCopyPhone = (phone: string, id: string) => {
    navigator.clipboard.writeText(phone);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleConfirmOrderDelivered = async () => {
    if (!deliveringOrderId) return;
    try {
      await confirmDelivered(
        deliveringOrderId,
        deliveryRef || `DEL-${Date.now()}`
      );
      setDeliveringOrderId(null);
      setDeliveryRef('');
    } catch (err: any) {
      alert(err.message || 'Delivery confirmation failed');
    }
  };

  const filteredOrders = orders.filter((ord) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') {
      return (
        ord.fulfillment_status === 'queued' ||
        (!ord.claimed_by && ord.fulfillment_status !== 'completed' && ord.fulfillment_status !== 'unable_to_deliver')
      );
    }
    if (statusFilter === 'processing') {
      return (
        ord.fulfillment_status === 'processing' ||
        Boolean(ord.claimed_by && ord.fulfillment_status !== 'completed' && ord.fulfillment_status !== 'unable_to_deliver')
      );
    }
    if (statusFilter === 'completed') {
      return ord.fulfillment_status === 'completed';
    }
    return true;
  });

  return (
    <div className="space-y-6 pb-28">
      {/* Title & Refresh */}
      <div className="px-1">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base sm:text-lg font-bold text-plum-dark flex items-center space-x-2 min-w-0">
            <ClipboardList className="w-5 h-5 text-primary shrink-0" />
            <span className="truncate">အော်ဒါ စာရင်း</span>
          </h2>

          <button
            onClick={refresh}
            title="အသစ်ပြန်တင်မည်"
            disabled={loading}
            className="p-1.5 sm:p-2 rounded-xl bg-surface-card border border-plum-border text-plum-dark hover:text-primary transition shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-1.5 overflow-x-auto pb-1 scrollbar-none">
        {[
          { id: 'all', label: 'အားလုံး', count: orders.length },
          {
            id: 'pending',
            label: 'ဆိုင်းငံ့',
            count: orders.filter(
              (o) =>
                o.fulfillment_status === 'queued' ||
                (!o.claimed_by && o.fulfillment_status !== 'completed' && o.fulfillment_status !== 'unable_to_deliver')
            ).length,
          },
          {
            id: 'processing',
            label: 'တာဝန်ယူထားဆဲ',
            count: orders.filter(
              (o) =>
                o.fulfillment_status === 'processing' ||
                Boolean(o.claimed_by && o.fulfillment_status !== 'completed' && o.fulfillment_status !== 'unable_to_deliver')
            ).length,
          },
          {
            id: 'completed',
            label: 'ပို့ဆောင်ပြီး',
            count: orders.filter((o) => o.fulfillment_status === 'completed').length,
          },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setStatusFilter(tab.id as any)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
              statusFilter === tab.id
                ? 'bg-primary text-white shadow-xs'
                : 'bg-surface-card border border-plum-border text-plum-muted hover:text-plum-dark'
            }`}
          >
            {tab.label} <span className="opacity-70 text-[10px]">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div className="space-y-3">
        {filteredOrders.map((ord) => {
          const isPending = !ord.claimed_by && ord.fulfillment_status !== 'completed' && ord.fulfillment_status !== 'unable_to_deliver';
          const isProcessing = Boolean(ord.claimed_by && ord.fulfillment_status !== 'completed' && ord.fulfillment_status !== 'unable_to_deliver');
          const isCompleted = ord.fulfillment_status === 'completed';
          const pkg = ord.package_id ? packages.find((p) => p.id === ord.package_id) : null;

          return (
            <div
              key={ord.id}
              className={`bg-surface-card rounded-2xl border p-4 shadow-xs space-y-3 transition ${
                isPending ? 'border-primary/40 shadow-sm' : 'border-plum-border'
              }`}
            >
              {/* Header: Operator & Status */}
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2.5">
                  <OperatorLogo id={ord.operator_id} size="md" className="rounded-xl shadow-xs" />
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm font-bold text-plum-dark tracking-wide">
                        {ord.recipient_phone}
                      </span>
                      <button
                        onClick={() => handleCopyPhone(ord.recipient_phone, ord.id)}
                        className="p-1 rounded-md bg-surface-page hover:bg-primary/20 text-plum-muted hover:text-primary transition"
                        title="ဖုန်းနံပါတ် ကူးယူရန်"
                      >
                        {copiedId === ord.id ? (
                          <Check className="w-3.5 h-3.5 text-status-green" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                    <p className="text-[11px] text-plum-muted">
                      အော်ဒါ အမှတ်: <span className="font-mono">{ord.id.slice(0, 10)}...</span>
                    </p>
                  </div>
                </div>

                <span
                  className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                    isCompleted
                      ? 'bg-green-500/10 text-status-green border-green-500/30'
                      : isProcessing
                      ? 'bg-sky-500/10 text-primary border-primary/30'
                      : 'bg-amber-500/10 text-status-amber border-amber-500/30'
                  }`}
                >
                  {isCompleted
                    ? '✅ ပို့ဆောင်ပြီး'
                    : isProcessing
                    ? '⚡️ တာဝန်ယူထားဆဲ'
                    : '⏳ စောင့်ဆိုင်း'}
                </span>
              </div>

              {/* Package / Airtime Details */}
              <div className="bg-surface-page p-2.5 rounded-xl border border-plum-border/60 flex items-center justify-between text-xs">
                <div>
                  <div className="flex items-center space-x-1.5">
                    {ord.order_type === 'phone_bill' && (
                      <span className="text-[10px] font-bold bg-sky-500/10 text-primary border border-sky-500/20 px-1.5 py-0.5 rounded-md">
                        📲 ဖုန်းဘေလ်
                      </span>
                    )}
                    <span className="font-semibold text-plum-dark">
                      {ord.order_type === 'phone_bill'
                        ? `${ord.operator_id.toUpperCase()} ${(ord.airtime_amount || ord.amount).toLocaleString()} Ks ဘေလ်`
                        : (pkg ? pkg.name : `${ord.operator_id.toUpperCase()} Package`)}
                    </span>
                  </div>
                  <span className="text-[11px] text-plum-muted block mt-0.5">
                    ငွေပေးချေမှု: {ord.payment_method?.toUpperCase()}
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-primary text-sm font-mono">
                    {ord.amount.toLocaleString()} Ks
                  </span>
                  <span className="text-[10px] text-plum-muted block">
                    {new Date(ord.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>

              {/* USSD Transfer Helper for Phone Bill */}
              {ord.order_type === 'phone_bill' && !isCompleted && (
                <div className="bg-primary/5 p-2 rounded-xl border border-primary/10 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-[10px] text-plum-muted block">⚡ USSD ဘေလ်လွှဲ Code:</span>
                    <span className="font-mono font-bold text-plum-dark">
                      {ord.operator_id === 'mpt'
                        ? `*223*${ord.airtime_amount || ord.amount}*${ord.recipient_phone}#`
                        : ord.operator_id === 'atom'
                        ? `*979*2*${ord.airtime_amount || ord.amount}*${ord.recipient_phone}#`
                        : ord.operator_id === 'mytel'
                        ? `*966*3*${ord.airtime_amount || ord.amount}*${ord.recipient_phone}#`
                        : `*133*${ord.airtime_amount || ord.amount}*${ord.recipient_phone}#`}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      const code = ord.operator_id === 'mpt'
                        ? `*223*${ord.airtime_amount || ord.amount}*${ord.recipient_phone}#`
                        : ord.operator_id === 'atom'
                        ? `*979*2*${ord.airtime_amount || ord.amount}*${ord.recipient_phone}#`
                        : ord.operator_id === 'mytel'
                        ? `*966*3*${ord.airtime_amount || ord.amount}*${ord.recipient_phone}#`
                        : `*133*${ord.airtime_amount || ord.amount}*${ord.recipient_phone}#`;
                      navigator.clipboard.writeText(code);
                      setCopiedId(`ussd-${ord.id}`);
                      setTimeout(() => setCopiedId(null), 1500);
                    }}
                    className="py-1 px-2.5 rounded-lg bg-surface-card border border-plum-border text-[11px] font-semibold text-primary hover:bg-primary/10 transition flex items-center space-x-1"
                  >
                    {copiedId === `ussd-${ord.id}` ? (
                      <>
                        <Check className="w-3 h-3 text-status-green" />
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy USSD</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Claimed Info */}
              {ord.claimed_by && (
                <p className="text-[11px] text-primary/80 font-medium">
                  👤 တာဝန်ယူထားသူ: <span className="font-semibold">{ord.claimed_by}</span>
                </p>
              )}

              {/* Actions */}
              {!isCompleted && (
                <div className="pt-2 border-t border-plum-border/60 flex items-center space-x-2">
                  {!ord.claimed_by ? (
                    <button
                      onClick={() => claimOrder(ord.id)}
                      disabled={actionLoading}
                      className="flex-1 bg-primary/15 hover:bg-primary/25 text-primary-light font-bold text-xs py-2.5 rounded-xl border border-primary/30 transition touch-target flex items-center justify-center space-x-1.5"
                    >
                      <span>🙋‍♂️ တာဝန်ယူမည် (Claim)</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setDeliveringOrderId(ord.id)}
                      className="flex-1 bg-primary hover:bg-primary-hover active:scale-[0.98] text-white font-bold text-xs py-2.5 rounded-xl transition shadow-xs touch-target flex items-center justify-center space-x-1.5"
                    >
                      <CheckCircle2 className="w-4 h-4 text-white" />
                      <span>✅ လွှဲပြီးကြောင်း အတည်ပြုမည်</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filteredOrders.length === 0 && (
          <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
            {statusFilter === 'all'
              ? 'အော်ဒါမှတ်တမ်း မရှိသေးပါ။'
              : 'ဤအခြေအနေရှိ အော်ဒါ မရှိပါ။'}
          </div>
        )}
      </div>

      {/* Confirm Delivery Modal */}
      {deliveringOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4">
            <h3 className="font-bold text-plum-dark text-base">Package လွှဲပြီးကြောင်း အတည်ပြုရန်</h3>
            <p className="text-xs text-plum-muted font-mono">Order: {deliveringOrderId}</p>

            <div>
              <label className="block text-xs font-semibold text-plum-dark mb-1">
                Delivery Reference / SMS ကုဒ်
              </label>
              <input
                type="text"
                value={deliveryRef}
                onChange={(e) => setDeliveryRef(e.target.value)}
                placeholder="ဥပမာ: SMS-TX-99824"
                className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2 text-xs font-mono text-plum-dark"
              />
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setDeliveringOrderId(null)}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
              >
                ပယ်ဖျက်မည်
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleConfirmOrderDelivered}
                className="flex-1 bg-primary text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-primary-hover transition"
              >
                {actionLoading ? 'အတည်ပြုနေပါသည်...' : 'အတည်ပြုပြီး အပြီးသတ်မည်'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
